import React, { useMemo } from 'react';
import type { Order, OrderStatus, RegisterSession, CurrentUser } from '../types';
import OrderStatusColumn from '../components/service/OrderStatusColumn';
import RegisterClosedGuard from '../components/pos/RegisterClosedGuard';
import { getCardCompletionStatus, getCardItemsWithTargets, getEffectiveQuantityCompletedForBatch, isKitchenRelevantCalendarDay, isStaleOnlineKitchenTicket } from '../lib/orderUtils';

interface ServicePageProps {
  orders: Order[];
  registerSession: RegisterSession | undefined;
  currentUser?: CurrentUser | null;
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  onUpdateItemStatus?: (orderId: string, itemIndex: number, served: boolean, targetQty?: number, batchIndex?: number, targetQtyForCard?: number) => void;
  /** Mark items completed (kitchen done); used by admin to move stuck "In Preparation" to Ready */
  onUpdateOrderItemStatus?: (orderId: string, itemIndex: number, status: 'pending' | 'completed', quantityCompleted?: number, batchIndex?: number, targetQtyForCard?: number) => void;
  onOpenRegister?: () => void;
}

const ServicePage: React.FC<ServicePageProps> = ({ orders, registerSession, currentUser, onUpdateStatus, onUpdateItemStatus, onUpdateOrderItemStatus, onOpenRegister }) => {
  // Filter out empty orders (orders with no items or only discount items)
  // Also filter out buffet orders (they don't need service), fully-served/closed orders,
  // and historical orders (only show today's tickets)
  const validOrders = orders.filter(o => {
    // Soft-hidden from KDS — keep Service in sync so tickets don't linger here
    if (o.hideFromKitchen) return false;
    // Buffet package itself is self-serve; Service only if there are add-ons to deliver
    if (o.orderType === 'buffet') {
      const extras = (o.items ?? []).some((item) => {
        if (item.removed || item.isBuffetPackage) return false;
        const isDiscount = item.price < 0 || String(item.id).includes('combo-discount');
        return !isDiscount;
      });
      if (!extras) return false;
    }
    // Takeaway uses "Order Picked Up" on POS only — do not show on Service screen
    if (o.orderType === 'takeaway') return false;
    // Filter out orders that are already fully closed from a service perspective
    if (o.status === 'served' || o.status === 'cancelled' || o.status === 'refunded' || o.status === 'comped') {
      return false;
    }
    // Filter out event/stall orders (handled on Mobile POS only)
    if (o.orderType === 'event') return false;
    // Online web orders: only after payment (no service tickets while customer is on HPP).
    if (o.orderType === 'online' && o.paymentStatus === 'unpaid') return false;
    // Match KDS: NZ calendar day (incl. online pickup day) and drop stale unpaid-progress online tickets
    if (!isKitchenRelevantCalendarDay(o) || isStaleOnlineKitchenTicket(o)) return false;
    
    // Filter out orders with no items
    if (!o.items || o.items.length === 0) return false;
    
    // Filter out orders that only have discount items (no actual items to serve)
    const hasPreparableItems = o.items.some(item => {
      const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
      return !isDiscount;
    });
    
    return hasPreparableItems;
  });
  
  // Per-card progression across Service columns:
  // New Orders: cards where no items are completed yet
  // In Preparation: cards where some but not all items are completed
  // Ready for Pickup: cards where all items are completed for that card, not all served
  const { newTickets, preparingTickets, readyTickets } = useMemo(() => {
    const newOut: Array<{ order: Order; batchIndex?: number; batchId?: string; showCompleteTable: boolean }> = [];
    const prep: Array<{ order: Order; batchIndex?: number; batchId?: string; showCompleteTable: boolean }> = [];
    const ready: Array<{ order: Order; batchIndex?: number; batchId?: string; showCompleteTable: boolean }> = [];

    validOrders.forEach((order) => {
      const snapshot = order.lastSentItemSnapshot ?? [];
      const items = order.items ?? [];
      const batches = order.orderBatches ?? [];
      const hasSnapshotAndDeltas =
        snapshot.length > 0 &&
        (() => {
          const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
          const currentNonRemoved = items.filter((i) => !i.removed);
          const hasNewOrDelta = currentNonRemoved.some((item, idx) => {
            const prev = item.lineId ? snapshotByLineId.get(item.lineId) : undefined;
            const prevByIndex = snapshot[idx];
            const prevQty = prev ?? prevByIndex;
            if (!prevQty) return true;
            return item.quantity > prevQty.quantity;
          });
          const hasRemoved =
            snapshot.length > currentNonRemoved.length ||
            snapshot.some((s) => !currentNonRemoved.some((i) => i.lineId === s.lineId));
          return hasNewOrDelta || hasRemoved;
        })();
      const hasMultipleBatches = batches.length > 1;
      const baseTicket = { order, batchIndex: undefined as number | undefined, batchId: undefined as string | undefined, showCompleteTable: true };
      const runningTickets =
        batches.length > 1
          ? batches.slice(1).map((batch, i) => ({
              order,
              batchIndex: i + 1,
              batchId: batch.batchId,
              showCompleteTable: false,
            }))
          : hasSnapshotAndDeltas
            ? [{ order, batchIndex: 1 as number, batchId: undefined as string | undefined, showCompleteTable: false }]
            : [];
      const allTickets = hasSnapshotAndDeltas || hasMultipleBatches ? [baseTicket, ...runningTickets] : [baseTicket];

      allTickets.forEach((ticket) => {
        const { items: cardItems, allCompleted, allServed } = getCardItemsWithTargets(order, ticket.batchIndex);
        if (cardItems.length === 0 || allServed) return;
        const cardBatchIndex = ticket.batchIndex != null ? ticket.batchIndex : 0;
        const anyCompleted = cardItems.some(({ item, targetQty }) =>
          getEffectiveQuantityCompletedForBatch(item, cardBatchIndex, targetQty) > 0
        );

        if (!anyCompleted && !allServed) {
          newOut.push(ticket);
        } else if (anyCompleted && !allCompleted && !allServed) {
          prep.push(ticket);
        } else if (allCompleted && !allServed) {
          ready.push(ticket);
        }
      });
    });

    return { newTickets: newOut, preparingTickets: prep, readyTickets: ready };
  }, [validOrders]);

  const handleMarkAllServed = () => {
    if (!onUpdateItemStatus) return;
    readyTickets.forEach(({ order, batchIndex }) => {
      const { items: cardItems } = getCardItemsWithTargets(order, batchIndex);
      cardItems.forEach(({ originalIndex, targetQty }) => {
        onUpdateItemStatus(order.id, originalIndex, true, targetQty, batchIndex, targetQty);
      });
    });
  };

  /** Move all "In Preparation" tickets to Ready for Pickup by marking their items completed (clears stuck orders not visible on KDS). */
  const handleMarkAllInPreparationAsReady = () => {
    if (!onUpdateOrderItemStatus) return;
    preparingTickets.forEach(({ order, batchIndex }) => {
      const { items: cardItems } = getCardItemsWithTargets(order, batchIndex);
      cardItems.forEach(({ originalIndex, targetQty }) => {
        onUpdateOrderItemStatus(order.id, originalIndex, 'completed', targetQty, batchIndex, targetQty);
      });
    });
  };

  // Show guard if register is closed
  if (!registerSession) {
    return (
      <RegisterClosedGuard onOpenRegister={onOpenRegister || (() => {})} />
    );
  }

  return (
    <div className="flex flex-col h-full">
        <header className="bg-white shadow-md z-10 flex-shrink-0">
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <h1 className="text-2xl font-bold text-gray-800">Service Screen</h1>
                    {currentUser?.isAdmin && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleMarkAllInPreparationAsReady}
                          className="px-3 py-1.5 text-sm font-medium rounded-md bg-slate-600 text-white hover:bg-slate-700"
                          title="Move all In Preparation tickets to Ready for Pickup (e.g. orders stuck when not visible on Kitchen)"
                        >
                          Mark all in preparation as ready
                        </button>
                        <button
                          type="button"
                          onClick={handleMarkAllServed}
                          className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
                        >
                          Mark all served
                        </button>
                      </div>
                    )}
                </div>
            </div>
        </header>
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-hidden bg-gray-200">
            <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6 h-full min-h-0 w-full">
                <OrderStatusColumn title="New Orders" tickets={newTickets} onUpdateStatus={onUpdateStatus} onUpdateItemStatus={onUpdateItemStatus} />
                <OrderStatusColumn title="In Preparation" tickets={preparingTickets} onUpdateStatus={onUpdateStatus} onUpdateItemStatus={onUpdateItemStatus} />
                <OrderStatusColumn title="Ready for Pickup" tickets={readyTickets} onUpdateStatus={onUpdateStatus} onUpdateItemStatus={onUpdateItemStatus} />
            </div>
        </main>
    </div>
  );
};

export default ServicePage;
